import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '../../lib/api';
import { Button, Card, Input, Textarea, Select } from '../../components/ui';

type AnnouncementChannel = 'in-app' | 'push' | 'email';

interface Announcement {
  id: string;
  subject: string;
  body: string;
  priority: 'low' | 'normal' | 'high';
  sentBy: {
    id: string;
    name: string;
    email: string;
  };
  recipientCount: number;
  sentAt: string;
  deliveryStatus: {
    channels?: AnnouncementChannel[];
    inApp?: { delivered: boolean };
    push?: { attempted: boolean };
    email?: { sent: number; failed: number; failedEmails: string[] };
    // legacy shape
    sent?: number;
    failed?: number;
    failedEmails?: string[];
  };
}

interface AnnouncementFormData {
  subject: string;
  body: string;
  priority: 'low' | 'normal' | 'high';
  channels: AnnouncementChannel[];
  displayDuration: number; // minutes
  imageUrl: string;
}

export default function AnnouncementComposer() {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState<AnnouncementFormData>({
    subject: '',
    body: '',
    priority: 'normal',
    channels: ['in-app', 'push'],
    displayDuration: 60,
    imageUrl: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof AnnouncementFormData | 'channels', string>>>({});
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Fetch announcement history
  const { data: announcements, isLoading: announcementsLoading } = useQuery<Announcement[]>({
    queryKey: ['admin', 'announcements'],
    queryFn: () => fetchApi<Announcement[]>('/admin/announcements'),
  });

  // Send announcement mutation
  const sendMutation = useMutation({
    mutationFn: (data: AnnouncementFormData) =>
      fetchApi('/admin/announcements', {
        method: 'POST',
        body: JSON.stringify({
          ...data,
          imageUrl: data.imageUrl.trim() || null,
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'announcements'] });
      showNotification('success', 'Announcement sent successfully');
      resetForm();
    },
    onError: (error: Error) => {
      showNotification('error', `Failed to send announcement: ${error.message}`);
    },
  });

  // Delete announcement mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      fetchApi(`/admin/announcements/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'announcements'] });
      showNotification('success', 'Announcement deleted');
      setDeleteConfirmId(null);
    },
    onError: (error: Error) => {
      showNotification('error', `Failed to delete: ${error.message}`);
      setDeleteConfirmId(null);
    },
  });

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const toggleChannel = (channel: AnnouncementChannel) => {
    setFormData((prev) => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter((c) => c !== channel)
        : [...prev.channels, channel],
    }));
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof AnnouncementFormData | 'channels', string>> = {};

    if (!formData.subject.trim()) {
      errors.subject = 'Subject is required';
    }
    if (!formData.body.trim()) {
      errors.body = 'Message body is required';
    }
    if (formData.channels.length === 0) {
      errors.channels = 'At least one delivery channel must be selected';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    sendMutation.mutate(formData);
  };

  const resetForm = () => {
    setFormData({
      subject: '',
      body: '',
      priority: 'normal',
      channels: ['in-app', 'push'],
      displayDuration: 60,
      imageUrl: '',
    });
    setFormErrors({});
    setIsFormOpen(false);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getPriorityBadgeColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (announcementsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-usc-garnet mx-auto"></div>
          <p className="mt-4 text-usc-gray-600">Loading announcements...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-usc-garnet">Announcements</h2>
          <p className="text-usc-gray-600 mt-2">Send announcements to all registered attendees</p>
        </div>
        <Button onClick={() => setIsFormOpen(true)}>
          + Compose Announcement
        </Button>
      </div>

      {/* Notification Toast */}
      {notification && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            notification.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {notification.message}
        </div>
      )}

      {/* Announcement Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-2xl font-bold text-usc-garnet mb-6">
                Compose Announcement
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="Subject *"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  error={formErrors.subject}
                  placeholder="Announcement subject"
                />

                <Textarea
                  label="Message Body *"
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  error={formErrors.body}
                  placeholder="Enter your announcement message..."
                  rows={8}
                />

                <div>
                  <Input
                    label="Image URL (optional)"
                    value={formData.imageUrl}
                    onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                    type="url"
                  />
                  {formData.imageUrl.trim() && (
                    <div className="mt-2 rounded-lg overflow-hidden border border-gray-200">
                      <img
                        src={formData.imageUrl.trim()}
                        alt="Preview"
                        className="w-full max-h-48 object-cover"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        onLoad={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'block'; }}
                      />
                    </div>
                  )}
                  <p className="mt-1 text-xs text-usc-gray-500">
                    Displayed in the web banner and as a rich push notification image on iOS.
                  </p>
                </div>

                <Select
                  label="Priority"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value as 'low' | 'normal' | 'high' })}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                </Select>

                {/* Delivery Channels */}
                <div>
                  <p className="text-sm font-medium text-usc-gray-700 mb-2">Delivery Channels *</p>
                  <div className="flex flex-wrap gap-4">
                    {(
                      [
                        { value: 'in-app', label: 'In-App', description: 'Web banner + iOS live notification' },
                        { value: 'push', label: 'Mobile Push', description: 'APNs to iOS devices' },
                        { value: 'email', label: 'Email', description: 'Email to all attendees' },
                      ] as { value: AnnouncementChannel; label: string; description: string }[]
                    ).map(({ value, label, description }) => {
                      const checked = formData.channels.includes(value);
                      return (
                        <label
                          key={value}
                          className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                            checked
                              ? 'border-usc-garnet bg-red-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5 h-4 w-4 accent-usc-garnet"
                            checked={checked}
                            onChange={() => toggleChannel(value)}
                          />
                          <div>
                            <span className="text-sm font-semibold text-usc-gray-900">{label}</span>
                            <p className="text-xs text-usc-gray-500">{description}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {formErrors.channels && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.channels}</p>
                  )}
                </div>

                {formData.channels.includes('push') && (
                  <Select
                    label="Banner Display Duration"
                    value={String(formData.displayDuration)}
                    onChange={(e) => setFormData({ ...formData, displayDuration: Number(e.target.value) })}
                  >
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="60">1 hour</option>
                    <option value="120">2 hours</option>
                    <option value="240">4 hours</option>
                    <option value="480">8 hours (all day)</option>
                  </Select>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={sendMutation.isPending}
                  >
                    {sendMutation.isPending ? 'Sending...' : 'Send Announcement'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {/* Announcement History */}
      <div className="space-y-4">
        <h3 className="text-xl font-bold text-usc-gray-900 mb-4">Announcement History</h3>
        
        {announcements && announcements.length > 0 ? (
          announcements.map((announcement) => (
            <Card key={announcement.id} className="p-6 relative">
              {/* Delete confirmation overlay */}
              {deleteConfirmId === announcement.id && (
                <div className="absolute inset-0 bg-white bg-opacity-95 rounded-lg flex items-center justify-center z-10">
                  <div className="text-center p-6">
                    <p className="text-lg font-medium text-usc-gray-900 mb-2">Delete this announcement?</p>
                    <p className="text-sm text-usc-gray-600 mb-4">"{announcement.subject}" will be permanently removed.</p>
                    <div className="flex justify-center gap-3">
                      <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
                        Cancel
                      </Button>
                      <Button
                        onClick={() => deleteMutation.mutate(announcement.id)}
                        disabled={deleteMutation.isPending}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-usc-garnet">{announcement.subject}</h3>
                    <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getPriorityBadgeColor(announcement.priority)}`}>
                      {announcement.priority.charAt(0).toUpperCase() + announcement.priority.slice(1)} Priority
                    </span>
                  </div>
                  
                  <p className="text-usc-gray-700 mb-4 whitespace-pre-wrap">{announcement.body}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="font-medium text-usc-gray-600">Sent by:</span>{' '}
                      <span className="text-usc-gray-900">{announcement.sentBy.name}</span>
                    </div>
                    <div>
                      <span className="font-medium text-usc-gray-600">Sent at:</span>{' '}
                      <span className="text-usc-gray-900">{formatDateTime(announcement.sentAt)}</span>
                    </div>
                    <div>
                      <span className="font-medium text-usc-gray-600">Recipients:</span>{' '}
                      <span className="text-usc-gray-900">{announcement.recipientCount}</span>
                    </div>
                    <div>
                      <span className="font-medium text-usc-gray-600">Channels:</span>{' '}
                      {announcement.deliveryStatus.channels ? (
                        <span className="text-usc-gray-900">
                          {announcement.deliveryStatus.channels.map((c) =>
                            c === 'in-app' ? 'In-App' : c === 'push' ? 'Mobile Push' : 'Email'
                          ).join(', ')}
                        </span>
                      ) : (
                        <span className="text-usc-gray-500 italic">email</span>
                      )}
                    </div>
                    {(announcement.deliveryStatus.email || announcement.deliveryStatus.sent !== undefined) && (
                      <div>
                        <span className="font-medium text-usc-gray-600">Email:</span>{' '}
                        <span className="text-green-700 font-medium">
                          {announcement.deliveryStatus.email?.sent ?? announcement.deliveryStatus.sent ?? 0} sent
                        </span>
                        {(announcement.deliveryStatus.email?.failed ?? announcement.deliveryStatus.failed ?? 0) > 0 && (
                          <span className="text-red-700 font-medium ml-2">
                            , {announcement.deliveryStatus.email?.failed ?? announcement.deliveryStatus.failed} failed
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {(() => {
                    const failedEmails =
                      announcement.deliveryStatus.email?.failedEmails ??
                      announcement.deliveryStatus.failedEmails ??
                      [];
                    return failedEmails.length > 0 ? (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm font-medium text-red-800 mb-2">Failed Email Deliveries:</p>
                        <ul className="text-sm text-red-700 list-disc list-inside">
                          {failedEmails.map((email, index) => (
                            <li key={index}>{email}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null;
                  })()}
                </div>
                <button
                  onClick={() => setDeleteConfirmId(announcement.id)}
                  className="ml-4 p-2 text-usc-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
                  title="Delete announcement"
                  aria-label={`Delete announcement: ${announcement.subject}`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </Card>
          ))
        ) : (
          <Card className="p-12 text-center">
            <p className="text-usc-gray-600 text-lg">No announcements sent yet</p>
            <p className="text-usc-gray-500 mt-2">Compose your first announcement to get started</p>
          </Card>
        )}
      </div>
    </div>
  );
}
